import { Camera, Scene, WebGLRenderer, InstancedMesh, MeshStandardMaterial, AmbientLight, DirectionalLight, Matrix4 } from 'three';
import { MercatorCoordinate } from 'maplibre-gl';
import { createPortalGeometries, uniquePortals, portalMatrix, PORTAL_WIDTH, PORTAL_MIN_ZOOM } from './tunnel-portal-geometry.js';
import { groundElevation } from './terrain.js';

/** Portalfargen. Dempet grå, tydelig mørkere enn terrengets #cfc7b6. */
export const PORTAL_COLOR = '#7a7468';

/** All portals share geometry and one renderer (four instanced draw calls).
 * Approximate architectural markers at mapped portals; not surveyed structures.
 */
export function createTunnelPortalLayer(data) {
  let renderer, scene, camera, origin, metreScale;
  const meshes = [], materials = [];
  let geometries, portals;
  // Custom layers drapes ikke av terrenget. 17 av 26 portaler står mer enn
  // 5 m over havet (høyeste 67 m), så uten dette blir de gravd ned i bakken
  // i det terrenget slås på – akkurat som busskuret.
  let grounded = false;
  return {
    id: 'tunnel-portals-3d', type: 'custom', renderingMode: '3d',
    onAdd(map, gl) {
      portals = uniquePortals(data);
      if (!portals.length) return;
      origin = MercatorCoordinate.fromLngLat(portals[0].coordinates);
      metreScale = origin.meterInMercatorCoordinateUnits();
      camera = new Camera(); scene = new Scene();
      scene.add(new AmbientLight(0xffffff, 1.5));
      const sun = new DirectionalLight(0xffe6c2, 2.5);
      sun.position.set(-.6, -.4, 1); scene.add(sun);
      geometries = createPortalGeometries();
      // Én dempet gråtone, mørkere enn terrenget (PALETTE.background er
      // #cfc7b6). Portalen skal LESES som "her går det i fjell", ikke stjele
      // oppmerksomhet fra ruta.
      const palette = { marker: new MeshStandardMaterial({ color: PORTAL_COLOR, roughness: 1 }) };
      for (const [name, geometry] of Object.entries(geometries)) {
        const mesh = new InstancedMesh(geometry, palette[name], portals.length);
        meshes.push(mesh); materials.push(palette[name]); scene.add(mesh);
      }
      this.place(null);
      renderer = new WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
      renderer.autoClear = false;
      this.map = map;
      map.triggerRepaint();
    },
    /**
     * Plasserer alle portal-instansene. `map` gitt = slå opp terrenghøyde.
     * Modellmatrisen er translate(origin) * scale(metreScale), så `local`
     * er meter relativt til origin – og siden origin ligger på havnivå er
     * local-z rett og slett portalens terrenghøyde (+4 cm klaring).
     * @returns {boolean} true når hver portal har fått en ekte høyde
     */
    place(map) {
      let all = true;
      for (const mesh of meshes) {
        for (let i = 0; i < portals.length; i++) {
          const p = portals[i];
          const h = map ? groundElevation(map, p.coordinates) : null;
          if (h === null) all = false;
          const merc = MercatorCoordinate.fromLngLat(p.coordinates);
          const local = [
            (merc.x - origin.x) / metreScale,
            (merc.y - origin.y) / metreScale,
            (h ?? 0) + .04,
          ];
          const matrix = portalMatrix(p.bearing, merc.meterInMercatorCoordinateUnits() / metreScale, local);
          matrix.multiply(new Matrix4().makeScale(p.width / PORTAL_WIDTH, 1, p.depth / 2.5));
          mesh.setMatrixAt(i, matrix);
        }
        mesh.instanceMatrix.needsUpdate = true;
        // Uten dette beholder instansene kula fra forrige høyde, og
        // frustum-culling kan gjemme portaler som faktisk er i bildet.
        mesh.computeBoundingSphere();
      }
      return all;
    },

    render(_gl, options) {
      if (!renderer || this.map.getZoom() < PORTAL_MIN_ZOOM) return;
      // queryTerrainElevation svarer null til DEM-flisen for punktet er
      // lastet, så vi prøver på nytt hver frame til alle 26 har svart.
      if (!grounded) grounded = this.place(this.map);
      const mvp = options?.defaultProjectionData?.mainMatrix ?? options?.modelViewProjectionMatrix ?? options;
      const model = new Matrix4().makeTranslation(origin.x, origin.y, origin.z)
        .multiply(new Matrix4().makeScale(metreScale, metreScale, metreScale));
      camera.projectionMatrix.fromArray(mvp).multiply(model);
      renderer.resetState();
      // Preserve map depth: buildings can occlude the portals normally.
      renderer.render(scene, camera);
      renderer.resetState();
    },
    onRemove() {
      for (const mesh of meshes) mesh.dispose();
      for (const geometry of Object.values(geometries ?? {})) geometry.dispose();
      for (const material of materials) material.dispose();
      renderer?.dispose();
      renderer = null;
      scene = null;
      geometries = null;
      meshes.length = 0;
      materials.length = 0;
      this.map = null;
    },
  };
}
